/**
 * PhotoLightbox — Full-screen photo viewer with navigation and delete.
 *
 * Features:
 *   - Dark backdrop, centered image
 *   - Left/right arrow navigation + keyboard (ArrowLeft, ArrowRight, Escape)
 *   - Caption and date below image
 *   - Delete button (with confirm) for users with edit permission
 *   - Photo counter (e.g., "3 / 12")
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MediaLinkDto } from '../../api/mediaLinks';
import { mediaSrc } from '../../api/mediaUrl';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import styles from './ProgressGallery.module.css';

export interface PhotoLightboxProps {
  photos: MediaLinkDto[];
  initialIndex: number;
  canDelete: boolean;
  onDelete: (mediaLinkGuid: string) => void;
  onClose: () => void;
}

export default function PhotoLightbox({
  photos,
  initialIndex,
  canDelete,
  onDelete,
  onClose,
}: PhotoLightboxProps) {
  const { t } = useAppTranslation('pages');
  const [index, setIndex] = useState(initialIndex);

  const photo = photos[index];
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(photos.length - 1, i + 1));
  }, [photos.length]);

  const handleDelete = useCallback(() => {
    if (!photo) return;
    if (!window.confirm(t('progressGallery.deleteConfirm'))) return;
    onDelete(photo.mediaLinkGuid);
    // If we deleted the last photo in the list, close the lightbox
    if (photos.length <= 1) {
      onClose();
    } else if (index >= photos.length - 1) {
      setIndex((i) => Math.max(0, i - 1));
    }
  }, [photo, photos.length, index, onDelete, onClose, t]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, goPrev, goNext]);

  if (!photo) return null;

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return createPortal(
    <div className={styles.lightboxOverlay} onClick={onClose}>
      <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
        <img
          className={styles.lightboxImage}
          src={mediaSrc(photo.storageKey) ?? undefined}
          alt={photo.label || ''}
          draggable={false}
        />

        {(photo.label || photo.createdAt) && (
          <div className={styles.lightboxCaption}>
            {photo.label && (
              <p className={styles.lightboxCaptionText}>{photo.label}</p>
            )}
            <p className={styles.lightboxDate}>{fmtDate(photo.createdAt)}</p>
          </div>
        )}
      </div>

      {/* Close */}
      <CloseButton onClick={onClose} size="lg" variant="overlay" className={styles.lightboxClose} />

      {/* Navigation arrows */}
      {hasPrev && (
        <button
          className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
        >
          &#8249;
        </button>
      )}
      {hasNext && (
        <button
          className={`${styles.lightboxNav} ${styles.lightboxNext}`}
          onClick={(e) => { e.stopPropagation(); goNext(); }}
        >
          &#8250;
        </button>
      )}

      {/* Counter */}
      <span className={styles.lightboxCounter}>
        {index + 1} / {photos.length}
      </span>

      {/* Delete moved to three-dot context menu on thumbnail */}
    </div>,
    document.body
  );
}
